






























import { KeyboardKeyCode } from '@/types';
import { ObjectUtils } from '@/utils/objectUtils';
import Vue from 'vue';

export default Vue.extend({
  name: 'MStorePageRange',
  props: {
    value: {
      type: String
    },
    totalPages: {
      type: Number,
      required: true
    },
    isValid: {
      type: Boolean,
      required: false
    },
    isContiguous: {
      type: Boolean,
      required: false
    },
    selectAllPages: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    isReadonly: {
      type: Boolean,
      default: false
    },
    updateValueOnPageNavigation: {
      type: Boolean,
      default: true
    },
    previousPageNavigationIsEnabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isAllPages: false,
      isDirty: false
    };
  },
  computed: {
    inputValue: {
      get(this: any) {
        return this.value;
      },
      set(this: any, value: string) {
        this.$emit('input', value);
      }
    },
    isValidFormat(this: any) {
      const value = this.validate(this.inputValue);
      this.$emit('update:isValid', value);
      return value;
    },
    isContiguousFormat(this: any) {
      const value = (this.startValue === 1 && this.endValue === 1) || (!this.isCustomRange && this.isRangeFormat(this.inputValue) && this.isValidContiguousFormat(this.startValue, this.endValue));
      this.$emit('update:isContiguous', value);
      return value;
    },
    startValue(this: any): number | null {
      return this.getValueFromPageRange(this.inputValue, 0);
    },
    endValue(this: any): number | null {
      return this.getValueFromPageRange(this.inputValue, 1);
    },
    isCustomRange(this: any) {
      return this.inputValue?.includes(',');
    },
    allPagesString(this: any) {
      return this.getRangeString(1, this.totalPages);
    },
    contiguousAllPagesString(this: any): string {
      if (this.totalPages === 0 && this.isDisabled) {
        return '1-?';
      }
      return this.totalPages === 1 ? '1' : `1-${this.totalPages}`;
    },
    firstPageString(this: any): string {
      return '1';
    },
    isRightNavigationDisabled(this: any): boolean {
      return this.endValue >= this.totalPages || this.startValue !== 1 || !this.isContiguousFormat;
    },
    isLeftNavigationDisabled(this: any): boolean {
      return !this.isContiguousFormat || (!this.previousPageNavigationIsEnabled && (this.endValue === this.startValue || this.startValue !== 1));
    }
  },
  methods: {
    onKeyDown(this: any, event: any): void {
      if (KeyboardKeyCode[event.keyCode]) {
        return;
      }
      if (!this.validateInput(event.key)) {
        event.preventDefault();
      }
    },
    validateInput(input: any): boolean {
      const validCharacters = /^[0-9-,]+$/;
      return validCharacters.test(input);
    },
    onAllPagesChange(this: any, allPages: any): void {
      this.inputValue = allPages ? this.contiguousAllPagesString : this.firstPageString;
      this.$emit('update:allPages', allPages);
    },
    onAllPagesChangeByLabel(this: any, allPages: any) {
      if (this.isDisabled) {
        return;
      }
      this.isAllPages = !allPages;
      this.onAllPagesChange(this.isAllPages);
    },
    onPageNavigationClick(this: any, navigation: number): void {
      const endPage = this.endValue + navigation;
      this.$emit('update:endPage', navigation);
      if (!this.updateValueOnPageNavigation || endPage <= 0) {
        return;
      }
      this.inputValue = this.startValue === 1 && endPage === 1 ? '1' : `${this.startValue}-${endPage}`;
      this.$nextTick(() => {
        this.isAllPages = this.totalPages === this.endValue;
      });
    },
    getValueFromPageRange(this: any, value: string, index: number): number | null {
      const valueFromRange = this.inputValue === '1' ? 1 : this.parseNumber(value?.split('-')[index]);
      return valueFromRange;
    },
    parseNumber(input: any): number | null {
      return Number.isNaN(input) ? null : Number(input);
    },
    getRangeString(this: any, rangeStart: number, rangeEnd: number): string {
      return `${this.getRangeArray(rangeStart, rangeEnd)}`;
    },
    getRangeArray(rangeStart: number, rangeEnd: number): number[] {
      return [...Array(rangeEnd - rangeStart + 1)].map((_, i) => rangeStart + i);
    },
    hasDuplicates(array: any): boolean {
      return new Set(array).size !== array.length;
    },
    isRangeFormat(this: any, value: string): boolean {
      const parts = value?.split('-');
      return parts?.length === 2 && this.isNumberArray(parts);
    },
    isNumberArray(this: any, array: any[]): boolean {
      return array.every((x: string) => !ObjectUtils.isNaN(x));
    },
    isValidContiguousFormat(this: any, startValue: number, endValue: number): boolean {
      return startValue > 0 && endValue <= this.totalPages && endValue > startValue;
    },
    toArrayFromString(value: string, seperator: string): string[] {
      return value.split(seperator);
    },
    hasValidFormat(this: any, value: string): boolean {
      if (!this.isCustomRange && !this.isContiguousFormat && ObjectUtils.isNaN(value)) {
        return false;
      }
      if (this.isContiguousFormat) {
        return true;
      }
      const inputStringArray = this.toArrayFromString(value, ',');
      const inputRangeArray = inputStringArray.filter((x: string) => x?.includes('-'));
      const inputCustomArray = inputStringArray.filter((x: string) => !x?.includes('-'));
      const isValidRanges = inputRangeArray.every((x: string) => {
        const start = this.getValueFromPageRange(x, 0);
        const end = this.getValueFromPageRange(x, 1);
        return this.isRangeFormat(x) && this.isValidContiguousFormat(start, end);
      });
      const isValidPageNumbers = this.isNumberArray(inputCustomArray) && !inputCustomArray.some((x: string) => Number(x) > this.totalPages);
      return inputRangeArray?.length > 0 ? isValidRanges && isValidPageNumbers : isValidPageNumbers;
    },
    hasDuplicatePages(this: any, value: string): boolean | undefined {
      if (!this.isCustomRange) {
        return false;
      }
      const inputStringArray = this.toArrayFromString(value, ',');
      const inputRangeArray = inputStringArray.filter((x: string) => x?.includes('-'));
      const inputCustomArray = inputStringArray.filter((x: string) => !x?.includes('-'));
      const inputRangePagesArray = inputRangeArray.map((x: string) => {
        const rangeString = x.split('-');
        return this.getRangeArray(Number(rangeString[0]), Number(rangeString[1]));
      });
      return (
        (inputRangePagesArray.length > 0 &&
          inputRangePagesArray?.some((x: any, index: number) => {
            const restOfTheElements = [...inputRangePagesArray];
            restOfTheElements.splice(index, 1);
            return (
              restOfTheElements.some((y) => {
                return y.some((z: number) => x.includes(z));
              }) || inputCustomArray.some((i: number) => x.includes(Number(i)))
            );
          })) ||
        this.hasDuplicates(inputCustomArray)
      );
    },
    validate(this: any, value: string): boolean {
      return !(
        !value ||
        this.totalPages <= 0 ||
        ObjectUtils.isNaN(this.totalPages) ||
        !this.validateInput(value) ||
        Number(value) > this.totalPages ||
        Number(value) <= 0 ||
        !this.hasValidFormat(value) ||
        this.hasDuplicatePages(value)
      );
    },
    initialise(this: any) {
      this.isAllPages = this.selectAllPages;
      this.inputValue = this.isAllPages ? this.contiguousAllPagesString : this.inputValue || this.firstPageString;
    }
  },
  mounted(this: any) {
    this.initialise();
    this.$emit('update:isValid', this.validate(this.value));
    this.$emit('update:isContiguous', this.isContiguous);
  },
  watch: {
    inputValue() {
      this.isDirty = true;
      this.isAllPages = this.inputValue === this.contiguousAllPagesString;
    },
    selectAllPages(value) {
      this.isAllPages = value;
      this.onAllPagesChange(value);
    },
    totalPages() {
      this.isAllPages = this.inputValue === this.contiguousAllPagesString;
    }
  }
});
